import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Badge,
  Alert,
} from 'react-bootstrap';
import {
  FaArrowLeft,
  FaClock,
  FaRupeeSign,
  FaBook,
  FaPlay,
  FaCertificate,
  FaUsers,
  FaChalkboardTeacher,
  FaChevronDown,
  FaChevronRight,
  FaCheckCircle,
  FaInfoCircle,
  FaStar,
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import Header from '../../components/common/Header';
import ContactModal from '../../components/common/ContactModal';
import CourseService from '../../services/courseService';
import StudentBatchService from '../../services/studentBatchService';
import '../../styles/StudentCourseDetails.css';

const StudentCourseDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [showContactModal, setShowContactModal] = useState(false);
  const [modules, setModules] = useState([]);
  const [openModules, setOpenModules] = useState({});
  const [hasBatch, setHasBatch] = useState(false);
  const [batchInfo, setBatchInfo] = useState(null);

  useEffect(() => {
    loadCourseDetails();
    checkStudentBatch();
    window.scrollTo(0, 0);
  }, [id]);

  const loadCourseDetails = async () => {
    try {
      setLoading(true);
      const response = await CourseService.getCourseById(id);
      setCourse(response.data);
      
      if (response.data?.long_description) {
        parseModules(response.data.long_description);
      }
    } catch (error) {
      toast.error('Failed to load course details');
      console.error(error);
      navigate('/student');
    } finally {
      setLoading(false);
    }
  };

  const checkStudentBatch = async () => {
    try {
      const response = await StudentBatchService.getStudentBatchForCourse(id);
      if (response.data && response.data.has_batch) {
        setHasBatch(true);
        setBatchInfo(response.data);
      }
    } catch (error) {
      console.error('Failed to check batch info:', error);
    }
  };

  // FIXED: Parse modules - captures ALL content between H3 tags
  const parseModules = (htmlContent) => {
    if (!htmlContent) return;
    
    // Create a temporary div to parse HTML
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = htmlContent;
    
    const h3Elements = tempDiv.querySelectorAll('h3');
    const parsedModules = [];
    
    h3Elements.forEach((h3, index) => {
      let content = '';
      let nextElement = h3.nextElementSibling;
      
      // Collect all content until next H3 tag
      while (nextElement && nextElement.tagName !== 'H3') {
        content += nextElement.outerHTML;
        nextElement = nextElement.nextElementSibling;
      }
      
      parsedModules.push({
        id: index,
        title: h3.textContent,
        content: content,
      });
    });
    
    setModules(parsedModules);
    
    // Initialize all modules as closed
    const initialOpenState = {};
    parsedModules.forEach((_, index) => {
      initialOpenState[index] = false;
    });
    setOpenModules(initialOpenState);
  };

  const toggleModule = (index) => {
    setOpenModules(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const handleRequestEnrollment = () => {
    setShowContactModal(true);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
    }).format(amount || 0);
  };
  

  if (loading) {
    return (
      <div>
        <Header />
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  if (!course) return null;

  return (
    <div>
      <Header />
      <div className="student-course-details">
        {/* Back Button */}
        <div className="back-nav">
          <Container>
            <Button
              variant="link"
              className="text-decoration-none p-0"
              onClick={() => navigate('/student')}
            >
              <FaArrowLeft className="me-2" /> Back to Dashboard
            </Button>
          </Container>
        </div>

        {/* Hero Section */}
        <section className="course-hero-section">
          <Container>
            <Row className="align-items-center">
              <Col lg={8}>
                <Badge bg={hasBatch ? "success" : "primary"} className="mb-3 px-3 py-2">
                  {hasBatch ? '✓ Enrolled' : '📖 Available Course'}
                </Badge>
                <h1 className="course-hero-title">{course.name}</h1>
                <p className="course-hero-description">{course.description}</p>
                <div className="course-meta">
                  <div className="meta-item">
                    <FaClock className="me-2" />
                    <span>{course.duration}</span>
                  </div>
                  <div className="meta-item">
                    <FaRupeeSign className="me-2" />
                    <span>{formatCurrency(course.price)}</span>
                  </div>
                  {hasBatch && batchInfo?.batch && (
                    <div className="meta-item">
                      <FaUsers className="me-2" />
                      <span>Batch: {batchInfo.batch.name}</span>
                    </div>
                  )}
                </div>
                {!hasBatch && (
                  <Button 
                    variant="primary" 
                    size="lg" 
                    className="enroll-btn-hero mt-3"
                    onClick={handleRequestEnrollment}
                  >
                    Request Enrollment
                  </Button>
                )}
              </Col>
              {course.image_url && (
                <Col lg={4} className="text-center">
                  <img 
                    src={course.image_url} 
                    alt={course.name} 
                    className="course-hero-img"
                  />
                </Col>
              )}
            </Row>
          </Container>
          <div className="hero-wave-bottom">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 120" preserveAspectRatio="none">
              <path fill="#ffffff" fillOpacity="1" d="M0,64L48,80C96,96,192,128,288,128C384,128,480,96,576,85.3C672,75,768,85,864,96C960,107,1056,117,1152,112C1248,107,1344,85,1392,74.7L1440,64L1440,120L1392,120C1344,120,1248,120,1152,120C1056,120,960,120,864,120C768,120,672,120,576,120C480,120,384,120,288,120C192,120,96,120,48,120L0,120Z"></path>
            </svg>
          </div>
        </section>

        <Container className="py-5">
          <Row>
            <Col lg={8}>
              {/* Tab Buttons */}
              <div className="tab-buttons mb-4">
                <button
                  className={`tab-btn ${activeTab === 'overview' ? 'active' : ''}`}
                  onClick={() => setActiveTab('overview')}
                >
                  <FaBook className="me-2" /> Overview
                </button>
                <button
                  className={`tab-btn ${activeTab === 'curriculum' ? 'active' : ''}`}
                  onClick={() => setActiveTab('curriculum')}
                >
                  <FaPlay className="me-2" /> Curriculum
                </button>
                <button
                  className={`tab-btn ${activeTab === 'features' ? 'active' : ''}`}
                  onClick={() => setActiveTab('features')}
                >
                  <FaStar className="me-2" /> Features
                </button>
              </div>

              {/* Overview Tab */}
              {activeTab === 'overview' && (
                <div className="content-card">
                  <h3>About This Course</h3>
                  <div className="course-description">
                    <p>{course.description}</p>
                  </div>
                  
                  {course.long_description && !modules.length && (
                    <div 
                      className="course-long-description mt-4"
                      dangerouslySetInnerHTML={{ __html: course.long_description }}
                    />
                  )}
                </div>
              )}

              {/* Curriculum Tab */}
              {activeTab === 'curriculum' && (
                <div className="content-card">
                  <h3>Course Curriculum</h3>
                  {modules.length > 0 ? (
                    <div className="modules-list">
                      {modules.map((module) => (
                        <div key={module.id} className="module-item">
                          <div
                            className="module-header"
                            onClick={() => toggleModule(module.id)}
                          >
                            <div className="module-header-left">
                              {openModules[module.id] ? (
                                <FaChevronDown className="chevron" />
                              ) : (
                                <FaChevronRight className="chevron" />
                              )}
                              <Badge bg="secondary" className="module-number">
                                Module {module.id + 1}
                              </Badge>
                              <h4 className="module-title">{module.title}</h4>
                            </div>
                          </div>
                          {openModules[module.id] && (
                            <div className="module-content">
                              <div dangerouslySetInnerHTML={{ __html: module.content }} />
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-muted text-center py-4">
                      No curriculum available yet. Please check back later.
                    </p>
                  )}
                </div>
              )}

              {/* Features Tab */}
              {activeTab === 'features' && (
                <div className="content-card">
                  <h3>Key Features</h3>
                  <Row className="g-3 mt-2">
                    <Col md={6}>
                      <div className="feature-item">
                        <FaCertificate className="feature-icon text-success" />
                        <div>
                          <strong>Industry Certificate</strong>
                          <p className="small text-muted mb-0">Get recognized certification</p>
                        </div>
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="feature-item">
                        <FaChalkboardTeacher className="feature-icon text-success" />
                        <div>
                          <strong>Expert Instructors</strong>
                          <p className="small text-muted mb-0">Learn from industry professionals</p>
                        </div>
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="feature-item">
                        <FaPlay className="feature-icon text-success" />
                        <div>
                          <strong>Hands-on Projects</strong>
                          <p className="small text-muted mb-0">Build real-world applications</p>
                        </div>
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="feature-item">
                        <FaUsers className="feature-icon text-success" />
                        <div>
                          <strong>Community Support</strong>
                          <p className="small text-muted mb-0">Connect with fellow learners</p>
                        </div>
                      </div>
                    </Col>
                  </Row>
                </div>
              )}
            </Col>

            <Col lg={4}>
              {/* Info Card */}
              <div className="info-card">
                <h5 className="info-card-title">Course Details</h5>
                <div className="info-card-body">
                  <div className="info-row">
                    <span className="info-label">📅 Duration</span>
                    <span className="info-value">{course.duration}</span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">💰 Price</span>
                    <span className="info-value">{formatCurrency(course.price)}</span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">📚 Category</span>
                    <span className="info-value">IT Training</span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">🎓 Level</span>
                    <span className="info-value">Beginner to Advanced</span>
                  </div>
                </div>
                
                {!hasBatch ? (
                  <Button 
                    variant="primary" 
                    className="w-100 mt-3 request-btn"
                    onClick={handleRequestEnrollment}
                  >
                    Request Enrollment
                  </Button>
                ) : (
                  <Alert variant="success" className="text-center mt-3 mb-0">
                    <FaCheckCircle className="me-2" />
                    You are enrolled in this course!
                  </Alert>
                )}
                
                <div className="info-note mt-3">
                  <FaInfoCircle className="me-1 text-muted" />
                  <small className="text-muted">
                    Online enrollment is handled offline. Click "Request Enrollment" and our team will contact you.
                  </small>
                </div>
              </div>
            </Col>
          </Row>
        </Container>

        {/* Contact Modal */}
        <ContactModal
          show={showContactModal}
          onHide={() => setShowContactModal(false)}
          courseName={course.name}
          courseId={course.id}
        />
      </div>
    </div>
  );
};

export default StudentCourseDetails;